// Every attempt to resolve the person's code, and what happened (FR-24).
import { Badge, Button, Group, Loader, Paper, Stack, Table, Text, Title } from "@mantine/core";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { SignedIn } from "../../shared/session/guards";
import { FieldBadges, useFormat } from "../../shared/ui/format";
import { FormError, Notice, WidePage } from "../../shared/ui/Page";

const PAGE = 25;
const BLOCKS_KEY = ["message-blocks"];

const OUTCOME_COLOUR: Record<Schemas["Access"]["outcome"], string> = {
  disclosed: "green",
  denied: "red",
  blocked: "orange",
  not_found: "gray",
  pending: "yellow",
  check_sent: "blue",
  check_confirmed: "green",
  payment_prompt: "blue",
  message_sent: "blue",
};

export function AccessHistoryPage() {
  return (
    <SignedIn>
      <AccessHistory />
    </SignedIn>
  );
}

function AccessHistory() {
  const { t } = useTranslation();
  const f = useFormat();
  const history = useInfiniteQuery({
    queryKey: ["access-history"],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/access-history", { params: { query: { limit: PAGE, ...(pageParam ? { before: pageParam } : {}) } } }),
      ),
    getNextPageParam: (last) => (last.items.length < PAGE ? undefined : last.items[last.items.length - 1]?.created_at),
  });
  const items = history.data?.pages.flatMap((p) => p.items) ?? [];
  // A person can stop one business's messages (SR-2026-10-28-06).
  const queryClient = useQueryClient();
  const blocks = useQuery({ queryKey: BLOCKS_KEY, queryFn: () => unwrap(api.GET("/me/message-blocks")) });
  const blocked = new Set((blocks.data?.items ?? []).map((c) => c.id));
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const setBlocked = async (company: Schemas["CompanySummary"], stop: boolean) => {
    setError(null);
    setNotice(null);
    const path = { params: { path: { company_id: company.id } } };
    const { error } = stop
      ? await api.PUT("/me/message-blocks/{company_id}", path)
      : await api.DELETE("/me/message-blocks/{company_id}", path);
    if (error) return setError(errorMessage(error, t("error.generic")));
    setNotice(t(stop ? "access.messages_stopped" : "access.messages_allowed", { name: company.name }));
    void queryClient.invalidateQueries({ queryKey: BLOCKS_KEY });
  };

  return (
    <WidePage title={t("access.title")} intro={t("access.intro")}>
      {notice && <Notice>{notice}</Notice>}
      {error && <FormError message={error} />}
      {(blocks.data?.items.length ?? 0) > 0 && (
        <Paper withBorder p="md" radius="md" data-testid="message-blocks">
          <Stack gap="xs">
            <Title order={2} size="h5">
              {t("access.blocked_title")}
            </Title>
            {blocks.data?.items.map((c) => (
              <Group key={c.id} justify="space-between">
                <Text size="sm">{c.name}</Text>
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => void setBlocked(c, false)}
                  aria-label={t("access.allow_messages_for", { name: c.name })}
                >
                  {t("access.allow_messages")}
                </Button>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}
      {history.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : history.isError && items.length === 0 ? (
        <FormError message={errorMessage(history.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed" data-testid="access-empty">
          {t("access.empty")}
        </Text>
      ) : (
        <>
          <Table.ScrollContainer minWidth={720}>
            <Table striped verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("access.time")}</Table.Th>
                  <Table.Th>{t("access.company")}</Table.Th>
                  <Table.Th>{t("access.channel")}</Table.Th>
                  <Table.Th>{t("access.purpose")}</Table.Th>
                  <Table.Th>{t("access.fields")}</Table.Th>
                  <Table.Th>{t("access.outcome")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((a) => (
                  <Table.Tr key={a.id} data-testid="access-row">
                    <Table.Td>{f.dateTime(a.created_at)}</Table.Td>
                    <Table.Td>{a.company.name}</Table.Td>
                    <Table.Td>{f.channel(a.channel)}</Table.Td>
                    <Table.Td>{a.purpose}</Table.Td>
                    <Table.Td>
                      <FieldBadges fields={a.fields} color="gray" />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Badge color={OUTCOME_COLOUR[a.outcome]}>{t(`access.outcome_${a.outcome}`)}</Badge>
                        {a.outcome === "message_sent" && !blocked.has(a.company.id) && (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="red"
                            onClick={() => void setBlocked(a.company, true)}
                            aria-label={t("access.stop_messages_for", { name: a.company.name })}
                          >
                            {t("access.stop_messages")}
                          </Button>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {history.isFetchNextPageError && <FormError message={errorMessage(history.error, t("error.generic"))} />}
          {history.hasNextPage ? (
            <Button
              variant="default"
              w="fit-content"
              loading={history.isFetchingNextPage}
              onClick={() => void history.fetchNextPage()}
            >
              {t("access.load_more")}
            </Button>
          ) : (
            <Text size="sm" c="dimmed">
              {t("access.end")}
            </Text>
          )}
        </>
      )}
    </WidePage>
  );
}
